import React, { useState } from 'react'
import Text from '@embracesbs/component-text'
import Button2 from '@embracesbs/component-button2/Button2'
import Icon from '@embracesbs/component-icon/Icon'
import Accordion from '@embracesbs/component-accordion/Accordion'
import { useTenants } from '../Context/TenantContext'
import { useApi, useAuth } from '../../Hooks'
import styled from '@emotion/styled'

interface HealthCheckStatus {
  name: string
  success: boolean
  message: string
  details: string
}

interface HealthCheckResult {
  success: boolean
  message: string
  details: string
  duration: string
  healthChecks: HealthCheckStatus[]
  additionalData: Record<string, any>
}

const DetailContent = styled.div<{ isSuccess?: boolean }>`
  padding: 16px;
  background-color: ${(props) => (props.isSuccess ? 'var(--tma-success-tint)' : 'var(--tma-danger-tint)')};
  border-radius: 4px;
  margin-bottom: 16px;
`

const HealthCheckContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid var(--tma-list-card-border);
  background-color: var(--tma-card-bg);
`

const ErrorContainer = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 4px;
  background-color: var(--tma-danger-tint);
  border: 1px solid var(--tma-danger-border);
`

const HealthCheck: React.FC = () => {
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()
  const { selectedTenantId } = useTenants()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [authResult, setAuthResult] = useState<HealthCheckResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const runAuthHealthCheck = () => {
    if (!selectedTenantId || !isLoggedIn) return

    setIsLoading(true)
    setError(null)
    setAuthResult(null)

    fetchApi({
      endpoint: `/api/tenant/${selectedTenantId}/healthcheck/auth`,
      method: 'GET'
    })
      .then((response) => {
        setAuthResult(response)
      })
      .catch((err) => {
        console.error('Health check failed:', err)

        if (err.response?.data) {
          const errorData = err.response.data
          setAuthResult(errorData)
        } else {
          // Only in case we don't have a response at all, set a generic error
          setError(err.message || 'Failed to run health check')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (!selectedTenantId) {
    return (
      <Text align='center' textColor='grey'>
        Please select a tenant to run health checks
      </Text>
    )
  }

  return (
    <div>
      <Text textStyle='h6' style={{ marginBottom: '16px' }}>
        Health Checks
      </Text>

      <HealthCheckContainer>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text textStyle='subtitle'>Microsoft Entra ID Authentication</Text>
            <Button2
              buttonSize='24px'
              onClick={runAuthHealthCheck}
              isLoading={isLoading}
              leftElement={<Icon iconName='ControlsPlayFilled' size={12} />}
              text=''
              type='button'
              variant='contained'
              tooltip='Run authentication health check'
            />
          </div>

          {authResult && authResult.healthChecks && authResult.healthChecks.length > 0 ? (
            <div style={{ marginTop: '16px' }}>
              {authResult.healthChecks.map((check, index) => (
                <Accordion
                  key={index}
                  id={`health-check-${index}`}
                  isFullWidth={true}
                  headerChildren={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 8px'
                      }}
                    >
                      <Text>{check.name}</Text>
                      <div style={{ width: '12px' }}></div>
                      <Icon
                        iconName={check.success ? 'CheckCircle1Filled' : 'AlertTriangleFilled'}
                        style={{ color: check.success ? 'var(--tma-status-ok)' : 'var(--tma-status-failed)' }}
                        size={16}
                      />
                    </div>
                  }
                  style={{
                    marginBottom: index < authResult.healthChecks.length - 1 ? '8px' : '0',
                    borderRadius: '4px'
                  }}
                >
                  <div>
                    <Text>{check.message}</Text>
                    {check.details && (
                      <DetailContent isSuccess={check.success}>
                        <Text textStyle='body2'>{check.details}</Text>
                      </DetailContent>
                    )}
                  </div>
                </Accordion>
              ))}
            </div>
          ) : authResult ? (
            <div style={{ marginTop: '16px' }}>
              <Accordion
                id='health-check-error'
                headerChildren={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 8px'
                    }}
                  >
                    <Text>Authentication Error</Text>
                    <div style={{ width: '12px' }}></div>
                    <Icon iconName='AlertTriangleFilled' style={{ color: 'var(--tma-status-failed)' }} size={16} />
                  </div>
                }
                style={{
                  borderRadius: '4px'
                }}
              >
                <div>
                  <Text>{authResult?.message}</Text>
                  {authResult?.details && (
                    <DetailContent>
                      <Text textStyle='body2'>
                        {(() => {
                          try {
                            const errorDetails = JSON.parse(authResult.details)
                            return errorDetails.error_description || authResult.details
                          } catch (e) {
                            return authResult.details
                          }
                        })()}
                      </Text>
                    </DetailContent>
                  )}
                </div>
              </Accordion>
            </div>
          ) : null}
        </div>
      </HealthCheckContainer>

      {error && (
        <ErrorContainer>
          <Text textStyle='h6' textColor='error'>
            Error
          </Text>
          <Text>{error}</Text>
        </ErrorContainer>
      )}
    </div>
  )
}

export default HealthCheck
